import { createSelector } from 'reselect';
import { UserSubscriptionType } from '../../../modules/core/constants';
import { FeatureTypes, MembershipType, } from './types';
export const selectSystem = (state) => state.system;
export const selectUser = (state) => state.system.user;
export const getUserId = createSelector(selectUser, (user) => Boolean(user) && user.id);
export const selectUserInstitutionMembership = (state) => {
    const { id } = state.system.user;
    return state.system.institution.institutionMemberships.find(({ userId }) => userId === id);
};
export const selectMeetingHistory = (state) => state.meetingHistory;
export const selectInstitution = (state) => state.system.institution;
export const selectIsInstitutionOwner = createSelector(selectUser, selectInstitution, (user, institution) => Boolean(user) &&
    institution &&
    Boolean(user.institutionsMemberships) &&
    Boolean(user.institutionsMemberships.some((membership) => membership.institutionId === institution.id &&
        membership.role === 'owner')));
export const canConnectStripe = createSelector(selectInstitution, selectIsInstitutionOwner, (institution, isOwner) => {
    return (!institution ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        isOwner ||
        !Boolean(institution.chargeOnBehalfOfInstitution));
});
export const selectRoom = (state) => state.system.room;
export const selectRooms = (state) => state.system.rooms;
export const selectPersonalRoom = createSelector(selectUser, selectRooms, (user, rooms) => rooms.find((room) => room.isPersonal &&
    room.roomMemberships.some((membership) => membership.role === 'admin' && membership.userId === user.id)));
export const selectPurchases = (state) => state.system.purchases;
export const selectBetaAccesses = createSelector(selectUser, selectInstitution, selectRoom, selectRooms, (user, institution, room, rooms) => {
    const betaAccesses = { sharedRooms: false };
    for (const betaAccess of [
        ...user.betaAccesses,
        ...(institution?.betaAccesses ?? []),
    ]) {
        betaAccesses[betaAccess.feature] = true;
    }
    if (rooms.length > 1 ||
        !room.roomMemberships.some(({ role, userId }) => role === 'admin' && userId === user.id)) {
        betaAccesses.sharedRooms = true;
    }
    return betaAccesses;
});
export const selectActivePurchase = createSelector(selectPurchases, (purchases) => purchases.find(({ isActive }) => isActive));
export const selectActiveTrialPurchase = createSelector(selectActivePurchase, (activePurchase) => {
    return activePurchase?.trialEnd &&
        new Date() < new Date(activePurchase.trialEnd)
        ? activePurchase
        : null;
});
/*
  Usage:
  Takes number institutionId param after state
  selectActiveInstitutionPurchase(state, institutionId)
*/
export const selectInstitutionActivePurchase = createSelector([selectPurchases, (state, institutionId) => institutionId], (purchases, institutionId) => {
    return purchases.find((purchase) => Boolean(purchase.isActive) && purchase.institutionId === institutionId);
});
export const selectLocationDetails = (state) => state.system.locationDetails;
const getIsExtensionsReleasedForProAndClinics = (user, institution) => {
    const extensionsFeatureKey = 'extensionsForProAndClinics';
    const features = institution?.globalFeatures ?? user?.globalFeatures ?? [];
    const marketplaceFeature = features.find((feature) => feature.featureName === extensionsFeatureKey);
    return Boolean(marketplaceFeature) ? marketplaceFeature.isEnabled : false;
};
export const canUseExtensions = (state) => {
    const user = selectUser(state);
    const institution = selectInstitution(state);
    const isExtensionsReleasedForProAndClinics = getIsExtensionsReleasedForProAndClinics(user, institution);
    if (Boolean(institution) && isExtensionsReleasedForProAndClinics) {
        return true;
    }
    else if (Boolean(institution) && !isExtensionsReleasedForProAndClinics) {
        return institution.betaAccesses.some((beta) => beta.feature === FeatureTypes.MARKETPLACE ||
            beta.feature === FeatureTypes.MARKETPLACE_FLAG);
    }
    if (Boolean(user) && isExtensionsReleasedForProAndClinics) {
        return (user.subscriptionType !== UserSubscriptionType.Free ||
            user.betaAccesses.some((beta) => beta.feature === FeatureTypes.MARKETPLACE ||
                beta.feature === FeatureTypes.MARKETPLACE_FLAG));
    }
    else if (Boolean(user) && !isExtensionsReleasedForProAndClinics) {
        return user.betaAccesses.some((beta) => beta.feature === FeatureTypes.MARKETPLACE ||
            beta.feature === FeatureTypes.MARKETPLACE_FLAG);
    }
    return false;
};
// @deprecated
export const canUseMarketplace = (state) => {
    const user = selectUser(state);
    const institution = selectInstitution(state);
    if (Boolean(institution) && Boolean(user)) {
        const ownerOrAdmin = user.institutionsMemberships.some((membership) => {
            return (membership.institutionId === institution.id &&
                [MembershipType.owner, MembershipType.admin].includes(membership.role));
        });
        if (!ownerOrAdmin) {
            return false;
        }
    }
    return canUseExtensions(state);
};
export const selectAnalyticsFilter = (state) => state.analytics.filter;
export const selectAnalyticsDatePeriod = (state) => state.analytics.datePeriod;
export const selectHasDayPass = createSelector(selectUser, selectActivePurchase, (user, activePurchase) => {
    if (!Boolean(user)) {
        return false;
    }
    return (Boolean(user.dayPassEndDate) &&
        !activePurchase &&
        new Date(user.dayPassEndDate).getTime() > Date.now());
});
export const selectSubscriptionType = createSelector(selectUser, (user) => {
    if (!Boolean(user)) {
        return null;
    }
    return Boolean(user.subscriptionType)
        ? user.subscriptionType
        : UserSubscriptionType.Free;
});
export const canUseExportMeetingHistory = createSelector(selectSubscriptionType, (subscriptionType) => {
    if (subscriptionType === UserSubscriptionType.Pro ||
        subscriptionType === UserSubscriptionType.Clinic) {
        return true;
    }
    return false;
});
export const selectTrialEndDate = createSelector(selectUser, selectPurchases, (user, purchases) => {
    const trialPurchase = purchases.find(({ trialEnd, isActive }) => Boolean(trialEnd) && isActive);
    const purchaseTrialEndDate = trialPurchase?.trialEnd;
    return user.trialEndDate ?? purchaseTrialEndDate ?? null;
});
export const selectIsTrialInFuture = createSelector(selectTrialEndDate, (trialEndDate) => new Date(trialEndDate) > new Date());
export const selectTrialDaysLeft = createSelector(selectTrialEndDate, selectIsTrialInFuture, (trialEndDate, isTrialInFuture) => {
    return isTrialInFuture
        ? Math.round(Math.abs((Date.now() - new Date(trialEndDate).getTime()) /
            (24 * 60 * 60 * 1000)))
        : 0;
});
export const selectIsTrial = createSelector(selectInstitution, selectIsTrialInFuture, selectActiveTrialPurchase, selectActivePurchase, (currentInstitution, isTrialInFuture, activeTrialPurchase, activePurchase) => {
    return (!currentInstitution &&
        ((isTrialInFuture && !activePurchase) || Boolean(activeTrialPurchase)));
});
export const canUsePaidFeatures = createSelector(selectSubscriptionType, (type) => type !== UserSubscriptionType.Free);
export const selectPlans = (state) => state.system.plans;
export const selectStates = (state) => state.system.states;
export const selectLanguages = (state) => state.system.languages;
export const selectPersonalData = (state) => state.system.personalData;
export const selectLocationsLicensed = (state) => state.system.locationsLicensed;
export const selectLocationsLicensedAsStates = createSelector(selectLocationsLicensed, (locationsLicensed) => locationsLicensed.map(({ state }) => state));
export const selectPersonalDataTitles = createSelector(selectPersonalData, (personalData) => (personalData ? personalData.titles : []));
export const selectPersonalDataSpecialities = createSelector(selectPersonalData, (personalData) => (personalData ? personalData.specialities : []));
export const selectPersonalBusinessTypes = createSelector(selectPersonalData, (personalData) => (personalData ? personalData['business-types'] : []));
export const selectAfterLoginPath = (state) => state.system.afterLoginPath;
export const selectExtensionsList = (state) => state.extensionFramework.extensionsList;
export const selectUserManageableFeatures = (state) => state.system.userManageableFeatures;
